import { FC, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Form as AntdForm } from 'antd'
import { Form, Button } from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Heading } from '@lmc/ui-kit/src/general'
import { CreateRotaPattern, HomeDto } from '@lmc/ts-common'
import useInstancesCreate from '../../hooks/useInstancesCreate'
import { useRotaPatternCreate } from '../../hooks/useRotaPatternCreate'
import { InitialState } from '../../components/ModalStepperForm'
import useRotaGenerated, { GeneratedRota } from '../../hooks/useRotaGenerated'
import useGenerate from '../../hooks/useGenerate'
import { areSameWeek } from '../../utils.datetime'
import { useSelector } from 'react-redux'
import { VISIT } from '../../constants'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useUserSkills from '../../hooks/useUserSkills'
import ResidentFormCard from './ResidentFormCard'
import InstanceFormCard from './InstanceFormCard'
import ShiftFormCard from './ShiftFormCard'
import PatternFormCard from './PatternFormCard'
import { ButtonsContainer } from 'screens/Rostering/components/ButtonsContainer'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { commonPatterns } from 'utils/enhancedTestIds'

type NewRotaPatternModalProps = {
  testId?: string
  onCancel: () => void
  type: 'visit' | 'shift'
  initialValues?: any
}

const NewRotaPatternModal: FC<NewRotaPatternModalProps> = ({
  testId,
  onCancel,
  type,
  initialValues = { reoccurenceTooglePattern: true, numberSupply: 1 },
}) => {
  const { t } = useTranslation()
  const baseId = testId || `new-rota-pattern-${type}`
  const [form] = AntdForm.useForm()
  const [formData, setFormData] = useState<InitialState>(initialValues)
  const { createPattern, isLoadingCreate } = useRotaPatternCreate({
    type,
  })
  const { createEvents } = useInstancesCreate()
  const { checkRotaGenerated } = useRotaGenerated()
  const { generateRota } = useGenerate({
    startDate: formData.patternStart || new Date().toISOString(),
  })
  const { users } = useUsersList({ active: true })
  const { visitTypes } = useVisitTypesList()
  const { shiftTypes } = useShiftTypesList()
  const flags = useFeatureFlags({
    flagNames: [
      'rostering_editing_limitations',
      'new_create_pattern_save_and_duplicate',
    ],
  })?.data
  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled
  const flagsNewCreatePatternSaveAndDuplicate =
    flags?.new_create_pattern_save_and_duplicate?.enabled
  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )
  const { allSkills } = useUserSkills()
  const [isModalVisible, setIsModalVisible] = useState(true)
  const [duplicatedData, setDuplicatedData] = useState<any>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const initialRender = useRef(true)

  useEffect(() => {
    if (
      initialRender.current &&
      initialValues &&
      Object.keys(initialValues).length > 0
    ) {
      form.setFieldsValue(initialValues)
      setFormData((prev) => ({ ...prev, ...initialValues }))
      if (initialValues.startTime) {
        form.setFieldValue('startTime', initialValues.startTime)
      }
      if (initialValues.endTime) {
        form.setFieldValue('endTime', initialValues.endTime)
      }
      initialRender.current = false
    }
  }, [])

  const handleFinish = async (generateEvents: boolean) => {
    const pattern: any = await createPattern({
      ...formData,
    } as CreateRotaPattern)

    if (pattern && pattern._id) {
      if (generateEvents) {
        createEvents?.({
          patternId: pattern._id,
        })
      }
      return true
    }

    return false
  }

  const onFinish = async (): Promise<boolean> => {
    const checkRotaGeneratedParams: GeneratedRota = {
      patternStartDate: formData.patternStart,
      patternEndDate: formData.hasPatternEndDate
        ? formData.patternEnd
        : undefined,
    }

    const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)
    const result = await handleFinish(hasRota)

    if (result) {
      if (
        !hasRota &&
        formData.patternStart &&
        areSameWeek(formData.patternStart, new Date())
      ) {
        generateRota({})
      }
      setIsModalVisible(false)
      onCancel()
    }

    return result
  }

  const handleFormValuesChange = (values: any) => {
    setFormData((prev) => ({ ...prev, ...values }))
  }

  const handleStartTimeChanged = (value?: string) => {
    if (formData.startTime !== value) {
      setFormData?.((prev: any) => ({ ...prev, startTime: value }))
    }
  }

  const handleEndTimeChanged = (value?: string) => {
    setFormData?.((prev: any) => ({ ...prev, endTime: value }))
  }

  const handleFrequencyValue = (value?: string) => {
    setFormData?.((prev: any) => ({ ...prev, frequency: value }))
  }

  const handleIntervalValue = (value?: number) => {
    setFormData?.((prev: any) => ({ ...prev, interval: value }))
  }

  const handleDaysValue = (value?: string[]) => {
    setFormData?.((prev: any) => ({ ...prev, specificDays: value }))
  }

  const handleSaveAndDuplicate = async () => {
    setIsSubmitting(true)
    try {
      await form.validateFields()

      const currentValues = form.getFieldsValue(true)

      // Check if events need to be generated
      const checkRotaGeneratedParams: GeneratedRota = {
        patternStartDate: formData.patternStart,
        patternEndDate: formData.hasPatternEndDate
          ? formData.patternEnd
          : undefined,
      }

      const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)
      const savedPattern = await handleFinish(hasRota)

      if (savedPattern) {
        const duplicateData = {
          ...currentValues,
          title: `${currentValues.title} (duplicate)`,
          id: undefined,
          patternId: undefined,
        }

        setDuplicatedData(duplicateData)

        setIsModalVisible(false)

        setTimeout(() => {
          setIsModalVisible(true)
        }, 100)
      }
    } catch (error) {
      console.error('Validation failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    try {
      const success = await form
        .validateFields()
        .then(() => form.submit())
        .then(() => true)
        .catch(() => false)

      if (success) {
        return true
      }
      return false
    } catch (error) {
      console.error('Error saving pattern:', error)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (duplicatedData && isModalVisible) {
      form.setFieldsValue(duplicatedData)
      setFormData((prev) => ({ ...prev, ...duplicatedData }))
      setDuplicatedData(null)
    }
  }, [duplicatedData, isModalVisible, form])

  return (
    <>
      <Modal
        data-testid={baseId}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          onCancel()
        }}
        title={<StyledHeading level={3} data-testid={`${baseId}-title`}>{t('ADD_ROTA_PATTERN')}</StyledHeading>}
        width={648}
        footer={
          <FlexContainer data-testid={`${baseId}-footer`}>
            <ButtonsContainer>
              <Button
                key="cancel"
                data-testid={commonPatterns.button(baseId, 'cancel')}
                onClick={() => {
                  setIsModalVisible(false)
                  onCancel()
                }}
              >
                {t('CANCEL')}
              </Button>
              {flagsNewCreatePatternSaveAndDuplicate && (
                <Button 
                  key="saveAndDuplicate" 
                  data-testid={commonPatterns.button(baseId, 'save-duplicate')}
                  onClick={handleSaveAndDuplicate}
                >
                  {t('SAVE_AND_DUPLICATE')}
                </Button>
              )}
              <Button
                key="submit"
                variant="primary"
                data-testid={commonPatterns.button(baseId, 'save')}
                onClick={handleSave}
                isLoading={isLoadingCreate}
                data-cy="btn-save-modal"
              >
                {t('SAVE')}
              </Button>
            </ButtonsContainer>
          </FlexContainer>
        }
      >
        <StyledForm
          data-testid={`${baseId}-form`}
          form={form}
          onFinish={onFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
        >
          {type === VISIT && (
            <>
              <ResidentFormCard
                testId={`${baseId}-resident-card`}
                form={form}
                home={home}
                setFormData={setFormData}
                onResidentChange={(residentId) => {
                  setFormData((prev) => ({ ...prev, subjectId: residentId }))
                }}
                formData={formData}
                isSubmitting={isSubmitting}
              />
              <InstanceFormCard
                testId={`${baseId}-instance-card`}
                form={form}
                formData={formData}
                setFormData={setFormData}
                visitTypes={visitTypes}
                users={users}
                allSkills={allSkills}
                flagsRosteringEditingLimitations={
                  flagsRosteringEditingLimitations
                }
                isSubmitting={isSubmitting}
              />
            </>
          )}
          {type !== VISIT && (
            <ShiftFormCard
              testId={`${baseId}-shift-card`}
              shiftTypes={shiftTypes}
              users={users}
              formData={formData}
              form={form}
              setFormData={setFormData}
              isSubmitting={isSubmitting}
            />
          )}
          <PatternFormCard
            testId={`${baseId}-pattern-card`}
            form={form}
            formData={formData}
            setFormData={setFormData}
            onStartTimeChanged={handleStartTimeChanged}
            onEndTimeChanged={handleEndTimeChanged}
            onFrequencyValue={handleFrequencyValue}
            onIntervalValue={handleIntervalValue}
            onDaysValue={handleDaysValue}
          />
        </StyledForm>
      </Modal>
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledHeading = styled(Heading)`
  text-transform: none;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export default NewRotaPatternModal
